import React from "react";
import {
  Grid,
  Typography,
  Alert,
  AlertTitle,
  Button,
  Stack,
  Divider,
  InputAdornment,
} from "@mui/material";
import {
  useGetBuyerCreditLimitQuery,
  useGetPartnersQuery,
} from "api/api-accounts";
import SkeletonLoad from "components/Common/SkeletonLoad";
import { Formik } from "formik";
import { currencyFormatter } from "helpers/currencyFormatter";
import type { Account } from "types";
import Input from "components/Common/Input";
import LoadingButton from "@mui/lab/LoadingButton";
import { useCreateLeadPartnerCreditLimitMutation } from "api/api-origination";
import valueCleaner from "helpers/valueCleaner";
import { toast } from "react-toastify";

import {
  CL_APPROVED,
  CL_REJECTED,
  CL_REQUESTED,
  PENDING_SALES,
  PRODUCTS,
  CL_DEFAULT,
} from "codes";

const AvailableCreditLimit = ({
  buyer,
  disabled,
  navigateTab,
}: {
  buyer: Account;
  seller?: Account;
  disabled?: boolean;
  navigateTab?: (index: number) => void;
}) => {
  const { profile, account_product } = buyer;

  const {
    data: availableCreditLimit,
    isFetching: isFetchingCreditLimit,
    status,
    refetch: refetchCL,
  } = useGetBuyerCreditLimitQuery(buyer?.id ?? "");

  const { data: partners, isFetching: isFetchingPartners } = useGetPartnersQuery(buyer?.id ?? "");

  const [
    createLeadPartnerCreditLimit,
    { isLoading: isLoadingCreateLeadPartnerCreditLimit },
  ] = useCreateLeadPartnerCreditLimitMutation();

  const CURRENCY = "USD";
  const SUCCESFUL_CL_CALL =
    !isFetchingCreditLimit && availableCreditLimit && status !== "rejected";
  const NO_CL = !isFetchingCreditLimit && status === "rejected";

  return (
    <>
      {isFetchingCreditLimit && <SkeletonLoad bars={10} />}

      {NO_CL && navigateTab && account_product === PRODUCTS.reverse_factoring && (
        <Formik
          enableReinitialize
          initialValues={{
            requested_amount: currencyFormatter({ amount: CL_DEFAULT }),
            allocations: partners
              ? partners.reduce<{ [key: string]: number }>(
                (acc, partner) => ({
                  ...acc,
                  [partner.id]: CL_DEFAULT / partners.length,
                }),
                {}
              )
              : {} as { [key: string]: number },
            allocationsManuallyEdited: false,
          }}
          onSubmit={async (values, { setSubmitting }) => {
            const requestedAmount = valueCleaner(values.requested_amount);
            const totalAllocation = Object.values(values.allocations).reduce(
              (acc: number, val) => acc + valueCleaner(String(val)),
              0
            );

            if (totalAllocation !== requestedAmount) {
              toast.error(
                "Total partner allocation must equal the requested amount."
              );
              setSubmitting(false);
              return;
            }

            try {
              await createLeadPartnerCreditLimit({
                lead_id: buyer.id,
                requested_amount: requestedAmount,
              }).unwrap();

              await Promise.all(
                Object.entries(values.allocations).map(
                  ([partnerId, amount]) =>
                    createLeadPartnerCreditLimit({
                      lead_id: partnerId,
                      requested_amount: valueCleaner(String(amount)),
                    }).unwrap()
                )
              );

              toast.success(`Credit Limit Submitted for ${buyer.name}`);
            } catch (e: any) {
              toast.error(e?.message || "An error occurred during submission");
            }
            setSubmitting(false);
          }}
        >
          {(formikProps) => {
            const { values, setFieldValue, handleSubmit, isSubmitting } = formikProps;
            const requestedAmountNum = valueCleaner(values.requested_amount);

            React.useEffect(() => {
              if (!values.allocationsManuallyEdited && partners?.length) {
                const equalShare = requestedAmountNum / partners.length || 0;
                partners.forEach((partner) => {
                  setFieldValue(`allocations.${partner.id}`, equalShare);
                });
              }
            }, [requestedAmountNum]);

            const totalAllocation = Object.values(values.allocations).reduce(
              (acc: number, val) => acc + (typeof val === "number" ? val : valueCleaner(String(val))),
              0
            );

            return (
              <Grid item xs={12}>
                <Alert severity={disabled ? "error" : "info"}>
                  <Stack spacing={2}>
                    <AlertTitle sx={{ fontSize: "1.5em", fontWeight: "bold" }}>
                      Request a Credit Limit{" "}
                      {disabled ? "(Please complete the Details to proceed)" : ""}
                    </AlertTitle>
                    <Divider />
                    <Typography fontSize="1.5em">
                      Please ensure that the Company Registration No in
                      <Button onClick={() => navigateTab(0)}>Registration Details</Button> & Annual
                      Revenue in
                      <Button onClick={() => navigateTab(1)}>Additional Details</Button> regarding{" "}
                      <strong>{buyer?.name}</strong> are completed to request a Credit Limit.
                    </Typography>

                    <Grid item xs={6}>
                      <Input
                        name="requested_amount"
                        label="Credit Limit"
                        type="money"
                        textfieldProps={{
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">{CURRENCY}</InputAdornment>
                            ),
                          },
                        }}
                        style={{ width: "100%" }}
                        disabled={disabled}
                        {...formikProps}
                        onChange={(e) => {
                          setFieldValue("requested_amount", e.target.value);
                        }}
                        value={values.requested_amount}
                      />
                    </Grid>

                    {!isFetchingPartners && (partners ?? []).length > 0 && (
                      <Stack spacing={1}>
                        <Divider />
                        <Typography fontWeight="bold" mt={2}>
                          Distribute Credit Limit to Partners (Total must equal requested amount)
                        </Typography>
                        {(partners ?? []).map((partner) => (
                          <Grid container spacing={1} alignItems="center" key={partner.id}>
                            <Grid item xs={6}>
                              <Typography>{partner.name}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Input
                                name={`allocations.${partner.id}`}
                                label=""
                                type="money"
                                textfieldProps={{
                                  InputProps: {
                                    startAdornment: (
                                      <InputAdornment position="start">{CURRENCY}</InputAdornment>
                                    ),
                                  },
                                }}
                                value={values.allocations[partner.id as string] || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setFieldValue(`allocations.${partner.id}`, val);
                                  // Mark allocations manually edited to prevent auto re-divide
                                  if (!values.allocationsManuallyEdited) {
                                    setFieldValue("allocationsManuallyEdited", true);
                                  }
                                }}
                                style={{ width: "100%" }}
                                disabled={disabled}
                              />
                            </Grid>
                          </Grid>
                        ))}
                        <Typography
                          color={totalAllocation !== requestedAmountNum ? "error" : "success.main"}
                        >
                          {totalAllocation === requestedAmountNum
                            ? `Total: ${currencyFormatter({ amount: totalAllocation })} — Allocation is balanced.`
                            : `Total: ${currencyFormatter({ amount: totalAllocation })} / ${currencyFormatter({
                              amount: requestedAmountNum,
                            })} — Please ensure the allocation matches the requested amount.`}
                        </Typography>
                      </Stack>
                    )}

                    <Stack direction="row" justifyContent="flex-end">
                      <LoadingButton
                        variant="contained"
                        loading={isLoadingCreateLeadPartnerCreditLimit || isSubmitting}
                        onClick={() => handleSubmit()}
                        disabled={
                          disabled || totalAllocation !== requestedAmountNum || isSubmitting
                        }
                      >
                        Submit
                      </LoadingButton>
                    </Stack>
                  </Stack>
                </Alert>
              </Grid>
            );
          }}
        </Formik>
      )}

      {SUCCESFUL_CL_CALL && (
        <Grid container spacing={2}>
          {(availableCreditLimit.status === CL_REQUESTED ||
            availableCreditLimit.status === PENDING_SALES) && (
              <Grid item xs={12}>
                <Typography fontWeight="bold" fontSize="1.2em" color="warning.main">
                  Requested Credit Limit: {availableCreditLimit.currency}{" "}
                  {currencyFormatter({ amount: availableCreditLimit.requested_amount || 0 })}
                </Typography>
              </Grid>
            )}

          {availableCreditLimit.status === CL_REJECTED && (
            <>
              <Grid item xs={12}>
                <Typography fontWeight="bold" fontSize="1.2em" color="error.main">
                  Credit Limit Rejected
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography fontWeight="bold" fontSize="1.2em">
                  Requested Credit Limit: {availableCreditLimit.currency}{" "}
                  {currencyFormatter({ amount: availableCreditLimit.requested_amount || 0 })}
                </Typography>
              </Grid>
            </>
          )}

          {availableCreditLimit.status === CL_APPROVED && (
            <>
              <Grid item xs={12}>
                <Typography fontWeight="bold" fontSize="1.2em" color="success.main">
                  Approved Credit Limit: {availableCreditLimit.currency}{" "}
                  {currencyFormatter({ amount: availableCreditLimit.approved_amount || 0 })}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography fontWeight="bold" fontSize="1.2em">
                  Requested Credit Limit: {availableCreditLimit.currency}{" "}
                  {currencyFormatter({ amount: availableCreditLimit.requested_amount || 0 })}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      )}
    </>
  );
};

export default AvailableCreditLimit;
